import React, { useState } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { NAV } from "../modules";
import ReadAloud from "./ReadAloud";
import { LogOut, Search, Bell, Menu, X, ShieldHalf, Languages } from "lucide-react";

const SCREEN_MAP = {
  "/app/dashboard": "dashboard", "/app/tasks": "tasks", "/app/kanban": "kanban",
  "/app/risk-heat-map": "risk-heat-map", "/app/use-cases": "use_cases",
  "/app/approvals": "approvals", "/app/registers": "dashboard", "/app/cyber-gov": "cyber_gov",
  "/app/ai-readiness": "ai_readiness", "/app/ai-projects": "ai_projects", "/app/regulatory": "regulatory",
  "/app/business-impact": "business_impact", "/app/kri": "kri", "/app/kpi": "kpi",
  "/app/business-value": "business_value", "/app/soc": "soc", "/app/app-perf": "app_perf",
  "/app/net-perf": "network_perf", "/app/soar": "soar", "/app/audit-log": "audit_log",
  "/app/users": "users", "/app/integrations": "integrations",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, toggle, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/"); };
  const screen = SCREEN_MAP[location.pathname] || "dashboard";

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2.5 px-4 h-16 border-b border-slate-100 flex-shrink-0">
        <div className="w-9 h-9 rounded-xl bg-teal-700 flex items-center justify-center flex-shrink-0">
          <ShieldHalf className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <div className="font-bold text-slate-900 leading-tight text-sm truncate">{t("appName")}</div>
          <div className="text-[11px] text-slate-400 leading-tight truncate">{t("appTagline")}</div>
        </div>
      </div>
      <nav className="flex-1 px-2.5 py-3 space-y-0.5 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-teal-700 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
            <span className="truncate">{t(item.labelKey)}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-2.5 py-3 border-t border-slate-100 flex-shrink-0">
        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors">
          <LogOut className="w-[18px] h-[18px]" />{t("logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      <aside className="hidden lg:flex fixed inset-y-0 start-0 w-64 bg-white border-e border-slate-200 flex-col z-30">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute inset-y-0 start-0 w-64 bg-white"><SidebarContent /></aside>
        </div>
      )}

      <div className="lg:ps-64">
        <header className="sticky top-0 z-20 h-16 bg-white/85 backdrop-blur border-b border-slate-200 flex items-center gap-3 px-4 lg:px-6">
          <button className="lg:hidden text-slate-600" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <div className="relative flex-1 max-w-md hidden sm:block">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input placeholder={t("search")} className="w-full ps-10 pe-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:bg-white" />
          </div>
          <div className="flex items-center gap-2 ms-auto">
            <button onClick={toggle} className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700">
              <Languages className="w-4 h-4" />{t("language")}
            </button>
            <button className="relative w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600">
              <Bell className="w-5 h-5" /><span className="absolute top-1.5 end-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <div className="flex items-center gap-2.5 ps-3 border-s border-slate-200">
              <div className="w-9 h-9 rounded-full bg-teal-700 text-white flex items-center justify-center text-sm font-semibold">{user?.initials || "SA"}</div>
              <div className="hidden sm:block leading-tight">
                <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
                <div className="text-xs text-slate-400">{user?.role}</div>
              </div>
            </div>
          </div>
        </header>

        <main className="p-4 lg:p-6 max-w-[1500px] mx-auto">
          <Outlet />
        </main>
      </div>

      <ReadAloud screen={screen} />
    </div>
  );
}
